import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {resolveUnitsFromTypedValues} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {
  StoichiometryStructuredTableMetadata,
  StoichiometryStructuredTableRowMetadata,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval} from 'notebook/tables/TableConstants';
import {EMPTY_TYPED_VALUE, isUnitReferenceTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';

export type StoichiometryConcentrationCellDependencies = [amountUnit: TypedValue | null];
interface StoichiometryConcentrationValues {
  amountUnit: MeasurementUnit | undefined;
}

function transformCellDependenciesToValues(
  cellDependencies: StoichiometryConcentrationCellDependencies,
  units: Array<MeasurementUnit>
): StoichiometryConcentrationValues {
  const [amountUnitTypedValue] = cellDependencies;
  const amountUnit = isUnitReferenceTypedValue(amountUnitTypedValue)
    ? units.find((unit) => unit.id === amountUnitTypedValue?.unitSummary.id)
    : undefined;
  return {
    amountUnit,
  };
}

const stoichiometryConcentrationCustomEval: CustomEval<{
  tableMetadata: StoichiometryStructuredTableMetadata;
  rowMetadata: StoichiometryStructuredTableRowMetadata;
}> = (
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
) => {
  // Load the Amount unit
  const {units, error: unitsResolutionError} = resolveUnitsFromTypedValues(
    cellDependencies,
    resolveFromResolutionQuery
  );
  // If the units aren't loaded, show the current value and don't lock the cell.
  if (unitsResolutionError != null) {
    return {val: typedValue, error: unitsResolutionError, locked: false};
  }
  const {amountUnit} = transformCellDependenciesToValues(
    cellDependencies as StoichiometryConcentrationCellDependencies,
    units ?? []
  );
  // If we still don't have the units as expected, show the current value and don't lock the cell.
  if (!amountUnit || isVolumeUnit(amountUnit)) {
    return {val: typedValue, error: null, locked: false};
  }
  // Otherwise, hide the values and lock the cell.
  return {val: EMPTY_TYPED_VALUE, error: null, locked: true};
};

export default stoichiometryConcentrationCustomEval;
